import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { parseGpsTime } from '../utils/traccar';
import { fetchAllGps } from '../../../utils/gpsProviders';
import { formatDateFR } from '../../shared/utils/helpers';
import EngineControlModal from '../../cars/components/EngineControlModal';
import VehicleStatsModal from './VehicleStatsModal';
import { sendTelegramNotification } from '../../../utils/telegram';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { reverseGeocode, addGeofenceEvent, getGeofenceHistory, evaluateCityChange, normalizeCity } from '../../../utils/geocoding';
import { appendTrackPoint, getTrack } from '../../../utils/gpsTrack';
import { getHoodOpen, getIgnitionOn } from '../utils/hoodSensor';
import { reportBreakdown } from '../../breakdowns/utils/breakdownService';
import { saveTrackPoint, fetchTrack } from '../../../utils/gpsTrackDb';
import { useAuth } from '../../../context/AuthContext';

// distance in km between two GPS coordinates using Haversine formula
const distanceKm = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // distance in km
};

// Human-readable duration from milliseconds (e.g. "2h 15m", "3j 4h", "45m").
const formatDuration = (ms, isAr = false) => {
  if (!ms || ms < 0) ms = 0;
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  const dU = isAr ? 'ي' : 'j';
  const hU = isAr ? 'س' : 'h';
  const mU = isAr ? 'د' : 'm';
  if (d > 0) return `${d}${dU} ${h}${hU}`;
  if (h > 0) return `${h}${hU} ${m}${mU}`;
  return `${m}${mU}`;
};

// Derive stop/movement status for a vehicle.
// "Moving" if current speed > 3 kph; otherwise stationary, with stop duration
// measured from the last recorded movement time.
const STOP_SPEED_NOISE_KPH = 5;      // at/below this speed = not moving
const STOP_FIX_STALE_MS = 180000;    // no GPS fix for >180s = treat as stopped

const getStopInfo = (car, settings, nowMs) => {
  const speed = Number(car.gpsSpeed) || 0;
  // Freshness of the last GPS fix (timezone-correct epoch ms).
  const lastFix = parseGpsTime(car.gpsUpdated);
  const fixAgeMs = lastFix ? Math.max(0, nowMs - lastFix) : Infinity;
  const stale = fixAgeMs > STOP_FIX_STALE_MS;

  // Moving ONLY when the fix is fresh AND speed is above the noise floor.
  // (A stale fix or near-zero speed means the vehicle is stopped.)
  const moving = !stale && speed > STOP_SPEED_NOISE_KPH;

  // Stop duration measured from the last real movement (fallback to last fix).
  const lastMovedTs = parseGpsTime(car.gpsLastMovedAt) || lastFix;
  const stoppedMs = moving || !lastMovedTs ? 0 : Math.max(0, nowMs - lastMovedTs);
  const thresholdMin = parseFloat(settings.gpsStopThresholdMin) || 30;
  const isLongStop = !moving && stoppedMs >= thresholdMin * 60000;

  return {
    moving,
    stale,
    lastMovedAt: lastMovedTs ? new Date(lastMovedTs).toISOString() : null,
    stoppedMs,
    isLongStop
  };
};

// Classify a vehicle into a fleet status for filtering.
//  offline → no position OR device not communicating for > timeout
//  moving  → communicating + speed above noise
//  idle    → communicating + stopped recently (engine likely still on)
//  parked  → communicating + stopped for ≥ the configured park threshold
// IMPORTANT: a stopped vehicle is NOT offline. Offline is decided by the
// last *communication* (provider online flag), NOT by a stale GPS fix time
// (a parked device keeps reporting while its fix timestamp can stay old).
const VEHICLE_OFFLINE_DEFAULT_MIN = 10;
const getVehicleStatus = (car, settings, nowMs) => {
  if (!car.gpsLat || !car.gpsLng) return 'offline';

  // Last communication time (when we last received data from the device).
  const lastSeen = parseGpsTime(car.gpsLastSeen) || parseGpsTime(car.gpsUpdated);
  const seenAgeMs = lastSeen ? Math.max(0, nowMs - lastSeen) : Infinity;
  const offlineMs = (parseFloat(settings.gpsOfflineMin) || VEHICLE_OFFLINE_DEFAULT_MIN) * 60000;

  // Trust the provider's online/offline flag first; only fall back to the
  // communication-timeout when that flag is unknown. Never use it for "online".
  const offline = car.gpsDeviceStatus === 'offline'
    ? true
    : car.gpsDeviceStatus === 'online'
      ? false
      : seenAgeMs > offlineMs;
  if (offline) return 'offline';

  const speed = Number(car.gpsSpeed) || 0;
  if (speed > STOP_SPEED_NOISE_KPH) return 'moving';
  const lastMovedTs = parseGpsTime(car.gpsLastMovedAt) || lastSeen;
  const stoppedMs = lastMovedTs ? Math.max(0, nowMs - lastMovedTs) : 0;
  const parkMs = (parseFloat(settings.gpsStopThresholdMin) || 30) * 60000;
  return stoppedMs >= parkMs ? 'parked' : 'idle';
};

// Real-time status → color (overridable via settings.statusColors).
const VEHICLE_STATUS_COLORS = { moving: '#16a34a', idle: '#f59e0b', parked: '#64748b', offline: '#4b5563' };
const statusColor = (key, settings = {}) => (settings.statusColors && settings.statusColors[key]) || VEHICLE_STATUS_COLORS[key] || '#64748b';

// Valid GPS coordinate? (finite, in range, not the null-island 0,0)
const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng) &&
  Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

// Hood / Capot (DI1/DI2) open-state detection lives in a shared, testable util.

// Clean a list of track points: drop invalid coords, sort by timestamp, drop
// consecutive duplicates. Returns [{lat,lng,t}].
const sanitizeTrack = (pts) => {
  const valid = (pts || [])
    .map(p => ({ lat: Number(p.lat), lng: Number(p.lng), t: Number(p.t) || 0 }))
    .filter(p => isValidLatLng(p.lat, p.lng));
  valid.sort((a, b) => a.t - b.t);
  const out = [];
  for (const p of valid) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.lat - p.lat) < 1e-6 && Math.abs(last.lng - p.lng) < 1e-6) continue;
    out.push(p);
  }
  return out;
};

// Downsample a long latlng array to ~max points (keeps first & last) for perf.
const downsampleLatLng = (arr, max = 1500) => {
  if (arr.length <= max) return arr;
  const step = Math.ceil(arr.length / max);
  const r = [];
  for (let i = 0; i < arr.length; i += step) r.push(arr[i]);
  if (r[r.length - 1] !== arr[arr.length - 1]) r.push(arr[arr.length - 1]);
  return r;
};

// Heuristic device-performance check → decide simplified ("lite") marker rendering.
const detectLowPerf = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const mobile = /Android|iPhone|iPad|iPod|Mobile|Opera Mini/i.test(ua);
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4; // GB (Chrome only; undefined elsewhere)
  return mobile || cores <= 4 || mem <= 4;
};

export default function GPSPage({ settings }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const { agencyId } = useAuth();

  // Map type options (label + icon). Order mirrors Google's Roadmap/Satellite/Hybrid/Terrain.
  const mapLayers = [
    { key: 'streets',   icon: '🗺️', label: isAr ? 'خريطة'      : 'Plan' },
    { key: 'satellite', icon: '🛰️', label: isAr ? 'قمر صناعي'  : 'Satellite' },
    { key: 'hybrid',    icon: '🌐', label: isAr ? 'مختلط'      : 'Hybride' },
    { key: 'terrain',   icon: '⛰️', label: isAr ? 'تضاريس'     : 'Relief' },
    { key: 'dark',      icon: '🌙', label: isAr ? 'داكن'       : 'Sombre' },
  ];

  const { data: cars, loading, update: updateCar } = useCollection('cars');
  const { add: addBreakdown } = useCollection('breakdowns');
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [pickingMode, setPickingMode] = useState(false);
  const [toast, setToast] = useState('');
  const [traccarStatus, setTraccarStatus] = useState({ connected: false, lastSync: null, error: null });
  const [activeLayer, setActiveLayer] = useState('streets');
  const [engineControlCar, setEngineControlCar] = useState(null);
  const [statsCar, setStatsCar] = useState(null);
  // Geofence events are still tracked & persisted (Telegram alerts active);
  // the on-screen history panel was removed, so no display state is needed.
  const [, setGeofenceHistory] = useState(() => getGeofenceHistory());
  // Ticks every 30s so stop durations / statuses stay live even when a parked
  // vehicle sends no new position updates.
  const [nowTick, setNowTick] = useState(Date.now());

  const tileLayersRef = useRef({});
  const riskRef = useRef({ id: null, speed: 0, t: 0 }); // for harsh accel/brake detection
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const pickingRef = useRef(false);
  const selectedCarIdRef = useRef(null);
  const carsRef = useRef([]);
  const updateCarRef = useRef(null);
  // Tracks cars currently in speed-alert state { [carId]: true } — prevents repeated notifications
  const speedAlertedRef = useRef({});
  const hoodAlertedRef = useRef({}); // per-car hood (capot / DI) open-state edge detection
  const ignitionStateRef = useRef({}); // per-car last known ignition (engine) on/off state
  // Tracks last geocoded state per car: { [carId]: { city, lat, lng } }
  const cityTrackerRef = useRef({});
  // Prevents overlapping reverse-geocode calls for the same vehicle (avoids races/duplicates)
  const geocodingInFlightRef = useRef({});

  // ── Flash Route (live) + History (playback) ──
  const [statusFilter, setStatusFilter] = useState('all'); // all|moving|idle|parked|offline
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [fsSidebarOpen, setFsSidebarOpen] = useState(true);
  const [liteMarkers, setLiteMarkers] = useState(() => detectLowPerf());
  const mapShellRef = useRef(null);
  const [flashRoute, setFlashRoute] = useState(false);
  const [flashPoints, setFlashPoints] = useState([]);
  const flashFitRef = useRef(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRange, setHistoryRange] = useState('24h'); // '24h' | '7d' | 'custom'
  const [historyFrom, setHistoryFrom] = useState(''); // datetime-local string
  const [historyTo, setHistoryTo] = useState('');     // datetime-local string
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [scrubIndex, setScrubIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const liveRouteRef = useRef(null);
  const historyLayerRef = useRef(null);
  const playRef = useRef({ timer: null, marker: null, line: null });
  const scrubMarkerRef = useRef(null);
  const agencyIdRef = useRef(null);

  useEffect(() => {
    pickingRef.current = pickingMode;
  }, [pickingMode]);

  useEffect(() => {
    selectedCarIdRef.current = selectedCarId;
  }, [selectedCarId]);

  useEffect(() => {
    carsRef.current = cars;
  }, [cars]);

  useEffect(() => {
    agencyIdRef.current = agencyId;
  }, [agencyId]);


  useEffect(() => {
    // Recompute live status frequently so a vehicle flips to STOPPED promptly
    // (and a stale fix is detected) without waiting for a DB change.
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    updateCarRef.current = updateCar;
  }, [updateCar]);

  // ========================================
  // TRACCAR AUTO-SYNC
  // ========================================
  useEffect(() => {
    // Enable condition per provider (existing GPSWOX/Traccar unchanged; Afoulki isolated).
    const provider = settings.gpsProvider || 'traccar';
    const enabled = provider === 'afoulki'
      ? (!!settings.afoulkiEnabled && !!settings.afoulkiUrl)
      : provider === 'iopgps'
      ? (!!settings.iopgpsEnabled && !!settings.iopgpsUrl)
      : (!!settings.traccarEnabled && !!settings.traccarUrl && !!settings.traccarEmail);
    if (!enabled) return;

    let isMounted = true;
    let intervalId = null;
    
    const syncTraccar = async () => {
      try {
        const result = await fetchAllGps(settings);
        if (!isMounted) return;
        
        if (!result.success) {
          setTraccarStatus({ connected: false, lastSync: null, error: result.error });
          return;
        }

        // Match Traccar devices with our cars (by traccarUniqueId)
        const currentCars = carsRef.current;
        let syncCount = 0;
        
        for (const device of result.data) {
          if (!device.position) continue;

          // Isolate each vehicle: one failing device must NOT abort the sync of
          // all the others (the bug — a single error stopped the whole loop).
          try {
          // Find matching car
          const matchingCar = currentCars.find(c =>
            c.traccarUniqueId &&
            (c.traccarUniqueId === device.uniqueId ||
             c.traccarUniqueId === String(device.id))
          );
          
          if (matchingCar) {
            // Displacement from last stored position, in meters (ignore GPS noise <15m).
            const _moveM = (matchingCar.gpsLat && matchingCar.gpsLng)
              ? distanceKm(matchingCar.gpsLat, matchingCar.gpsLng, device.position.latitude, device.position.longitude) * 1000
              : Infinity;
            const _significantMove = _moveM > 15;

            // IMPORTANT: refresh live status (speed + fix time) on EVERY poll so a
            // parked vehicle never stays stuck at its last moving speed/timestamp.
            {
              const updates = {
                gpsUpdated: device.position.fixTime || new Date().toISOString(),
                gpsLastSeen: new Date().toISOString(), // last communication (not GPS fix time)
                gpsSpeed: device.position.speed || 0,
                gpsCourse: device.position.course || 0,
                gpsAddress: device.position.address || '',
                gpsSource: 'traccar',
                gpsDeviceStatus: device.status
              };
              // Live position ALWAYS mirrors the provider's latest fix so the map
              // on the platform matches the provider exactly (fixes the "stuck at
              // old position" sync gap). Jitter control stays on the TRAIL only
              // (appendTrackPoint below is still gated by _significantMove).
              if (device.position.latitude && device.position.longitude) {
                updates.gpsLat = device.position.latitude;
                updates.gpsLng = device.position.longitude;
              }
              
              // ODOMETER: Track total km
              if (device.odometer !== null && device.odometer !== undefined) {
                updates.gpsOdometer = device.odometer; // Total km from device
                updates.gpsOdometerSource = 'sensor';
              } 
              // Calculate from GPS positions (Haversine)
              else if (matchingCar.gpsLat && matchingCar.gpsLng) {
                const distanceTraveled = distanceKm(
                  matchingCar.gpsLat, matchingCar.gpsLng,
                  device.position.latitude, device.position.longitude
                );
                // Only accumulate if reasonable movement (filter GPS noise)
                if (distanceTraveled > 0.05 && distanceTraveled < 500) { // > 50m, < 500km (avoid teleport noise)
                  updates.gpsOdometer = (matchingCar.gpsOdometer || 0) + distanceTraveled;
                  updates.gpsOdometerSource = 'calculated';
                  
                  // Track daily km
                  const today = new Date().toISOString().split('T')[0];
                  if (matchingCar.gpsKmDate === today) {
                    updates.gpsKmDaily = (matchingCar.gpsKmDaily || 0) + distanceTraveled;
                  } else {
                    // New day - reset daily counter
                    updates.gpsKmDaily = distanceTraveled;
                    updates.gpsKmDate = today;
                    if (matchingCar.gpsKmDaily) {
                      updates.gpsKmPrevDay = matchingCar.gpsKmDaily;
                    }
                  }
                }
              }
              
              // ── MOVEMENT DETECTION (time + speed + distance based) ─────
              // Moving only if speed is above noise OR there was a real (>15m) move.
              const curSpeed = Math.round(device.position.speed || 0);
              if (curSpeed > 5 || _significantMove) {
                updates.gpsLastMovedAt = new Date().toISOString();
              } else if (!matchingCar.gpsLastMovedAt) {
                // Baseline the first time we ever see this vehicle.
                updates.gpsLastMovedAt = new Date().toISOString();
              }
              // ───────────────────────────────────────────────────────────

              // Record a trail point on real movement (moving speed OR displacement).
              // Use the GPS FIX TIME (not poll time) so the route is ordered and
              // timed by when the vehicle was actually there. appendTrackPoint
              // de-dupes near-identical points, so the path stays clean and dense.
              const _fixTs = (() => {
                const raw = device.position.fixTime || device.position.deviceTime || device.position.serverTime;
                const ms = raw ? new Date(raw).getTime() : NaN;
                return Number.isFinite(ms) ? ms : Date.now();
              })();
              if (_significantMove || curSpeed > 3) {
                const storedNew = appendTrackPoint(
                  matchingCar.id,
                  device.position.latitude,
                  device.position.longitude,
                  curSpeed,
                  _fixTs
                );
                if (storedNew) {
                  saveTrackPoint({
                    agencyId: agencyIdRef.current,
                    vehicleId: matchingCar.id,
                    lat: device.position.latitude,
                    lng: device.position.longitude,
                    speed: curSpeed,
                    t: _fixTs
                  });
                }
              }

              if (updateCarRef.current) {
                await updateCarRef.current(matchingCar.id, updates);
              }
              syncCount++;

              // ── SPEED ALERT ─────────────────────────────────────────────
              // NOTE: GPSWOX API already returns speed in kph — NO conversion needed.
              const speedKmh = Math.round(device.position.speed || 0);
              const speedLimit = settings.gpsSpeedLimit ?? 120;
              const speedAlertEnabled = !!settings.gpsSpeedAlertEnabled;
              const speedAlertTelegram = settings.gpsSpeedAlertTelegram !== false;

              if (speedAlertEnabled) {
                const carKey = matchingCar.id;
                if (speedKmh > speedLimit) {
                  // Only alert once per continuous excess event
                  if (!speedAlertedRef.current[carKey]) {
                    speedAlertedRef.current[carKey] = true;
                    if (speedAlertTelegram) {
                      const carLabel = `${matchingCar.brand || ''} ${matchingCar.model || ''} (${matchingCar.plate || ''})`;
                      const msg = buildTelegramMessage('speed_alert', {
                        carLabel, speed: speedKmh, limit: speedLimit,
                        time: new Date().toLocaleString('fr-FR')
                      });
                      sendTelegramNotification(msg, 'speed_alert');
                    }
                  }
                } else {
                  // Speed back to normal — reset flag so next excess triggers a new alert
                  speedAlertedRef.current[carKey] = false;
                }
              }
              // ────────────────────────────────────────────────────────────

              // ── HOOD / CAPOT ALERT (DI1/DI2 sensor) ──────────────────────
              // ON by default for ALL vehicles — controlled only by the global
              // "Capot ouvert" toggle in Settings (sendTelegramNotification gates
              // it). Edge-detected (once per open event), re-arms when closed.
              {
                const hoodOpen = getHoodOpen(device, matchingCar.hoodSensorName);
                const carKey = matchingCar.id;
                if (hoodOpen === true) {
                  if (!hoodAlertedRef.current[carKey]) {
                    hoodAlertedRef.current[carKey] = true;
                    const carLabel = `${matchingCar.brand || ''} ${matchingCar.model || ''} (${matchingCar.plate || ''})`.trim();
                    const msg = buildTelegramMessage('hood_opened', {
                      carLabel,
                      time: new Date().toLocaleString(lang === 'ar' ? 'ar-MA' : 'fr-FR')
                    });
                    sendTelegramNotification(msg, 'hood_opened');
                  }
                } else if (hoodOpen === false) {
                  hoodAlertedRef.current[carKey] = false; // closed → next open re-alerts
                }
              }
              // ────────────────────────────────────────────────────────────

              // ── IGNITION / MOTEUR ALERT (ACC sensor) ─────────────────────
              // ON by default for ALL vehicles — controlled by the global
              // "Moteur (Ignition)" toggle in Settings. Fires a Telegram message
              // on every change (engine started / engine stopped). Edge-detected.
              {
                const ignOn = getIgnitionOn(device);
                const carKey = matchingCar.id;
                if (ignOn !== null) {
                  const prev = ignitionStateRef.current[carKey];
                  if (prev !== undefined && prev !== ignOn) {
                    const carLabel = `${matchingCar.brand || ''} ${matchingCar.model || ''} (${matchingCar.plate || ''})`.trim();
                    const msg = buildTelegramMessage('ignition', {
                      carLabel, on: ignOn,
                      time: new Date().toLocaleString(lang === 'ar' ? 'ar-MA' : 'fr-FR')
                    });
                    sendTelegramNotification(msg, 'ignition');
                  }
                  ignitionStateRef.current[carKey] = ignOn;
                }
              }
              // ────────────────────────────────────────────────────────────

              // ── CITY GEOFENCING ──────────────────────────────────────────
              if (settings.geoNotifEnabled) {
                const carId = matchingCar.id;
                const lat = device.position.latitude;
                const lng = device.position.longitude;
                const tracker = cityTrackerRef.current[carId] || {};

                // Only geocode if moved >500m from last geocoded position
                const movedEnough = !tracker.lat || !tracker.lng ||
                  distanceKm(tracker.lat, tracker.lng, lat, lng) > 0.5;

                // Skip if a geocode for this vehicle is already running (prevents
                // overlapping API calls from firing duplicate notifications).
                if (movedEnough && !geocodingInFlightRef.current[carId]) {
                  geocodingInFlightRef.current[carId] = true;
                  // Record the position immediately so the next sync cycle doesn't
                  // re-fire another geocode before this one resolves.
                  cityTrackerRef.current[carId] = { ...tracker, lat, lng };

                  const geocodeLang = lang === 'ar' ? 'ar' : 'fr';
                  // Run geocoding async — don't await to avoid blocking sync loop
                  reverseGeocode(lat, lng, geocodeLang).then(newCityRaw => {
                    geocodingInFlightRef.current[carId] = false;
                    if (!newCityRaw) return;
                    const newCity = newCityRaw.trim();

                    // Update in-memory position + city for this session.
                    cityTrackerRef.current[carId] = { city: newCity, lat, lng };

                    // Decision engine (persisted): handles baseline, "still here",
                    // transient-flip confirmation and per-city cooldown. Returns
                    // notify:true only for a genuine, confirmed, non-repeated change.
                    const decision = evaluateCityChange(carId, newCity, {
                      fallbackLast: matchingCar.gpsLastCity
                    });

                    if (!decision.notify) return;

                    // Confirmed change → align the DB's last city with it.
                    if (updateCarRef.current &&
                        normalizeCity(matchingCar.gpsLastCity || '') !== normalizeCity(decision.city)) {
                      updateCarRef.current(carId, { gpsLastCity: decision.city }).catch(() => {});
                    }

                    const prevCity = decision.prevCity;
                    const geoMode = settings.geoNotifMode || 'enter';

                    // Optional city filter (applies to notification only; the last
                    // known city is always tracked regardless).
                    const filterRaw = (settings.geoNotifCities || '').trim();
                    const filterList = filterRaw
                      ? filterRaw.split(',').map(c => c.trim().toLowerCase()).filter(Boolean)
                      : [];
                    const matchesFilter = filterList.length === 0 ||
                      filterList.includes(normalizeCity(newCity)) ||
                      filterList.includes(normalizeCity(prevCity));

                    if (!matchesFilter) return;

                    const plate = matchingCar.plate || '';
                    const brand = matchingCar.brand || '';
                    const model = matchingCar.model || '';
                    const carLabel = `${brand} ${model} (${plate})`.trim();
                    const ts = new Date();

                    const event = {
                      id: `geo-${carId}-${ts.getTime()}`,
                      plate, brand, model,
                      fromCity: prevCity,
                      toCity: newCity,
                      type: 'enter',
                      lat, lng,
                      timestamp: ts.toISOString()
                    };

                    const shouldNotifyEnter = geoMode === 'enter' || geoMode === 'both';
                    const shouldNotifyLeave = geoMode === 'leave' || geoMode === 'both';

                    if (shouldNotifyEnter || shouldNotifyLeave) {
                      // History (feeds the history panel / dashboard alerts channel)
                      addGeofenceEvent(event);
                      setGeofenceHistory(getGeofenceHistory());

                      // Telegram
                      if (settings.geoNotifTelegram !== false) {
                        const geoData = { carLabel, prevCity, newCity, time: ts.toLocaleString('fr-FR') };
                        if (shouldNotifyEnter && shouldNotifyLeave) {
                          sendTelegramNotification(buildTelegramMessage('geo_change', geoData), 'geo_entry');
                        } else if (shouldNotifyEnter) {
                          sendTelegramNotification(buildTelegramMessage('geo_enter', geoData), 'geo_entry');
                        } else if (shouldNotifyLeave) {
                          sendTelegramNotification(buildTelegramMessage('geo_leave', geoData), 'geo_leave');
                        }
                      }
                    }
                  }).catch(() => {
                    geocodingInFlightRef.current[carId] = false;
                  });
                }
              }
              // ────────────────────────────────────────────────────────────
            }
          }
          } catch (devErr) {
            console.warn('[GPS sync] vehicle skipped:', devErr?.message);
          }
        }

        setTraccarStatus({
          connected: true,
          lastSync: new Date(), 
          error: null,
          devicesCount: result.data.length,
          syncedCount: syncCount
        });
      } catch (err) {
        if (isMounted) {
          setTraccarStatus({ connected: false, lastSync: null, error: err.message });
        }
      }
    };
    
    // Initial sync
    syncTraccar();
    
    // Periodic sync
    const refreshSec = parseInt(settings.traccarRefreshSec) || 30;
    intervalId = setInterval(syncTraccar, refreshSec * 1000);
    
    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [settings.gpsProvider, settings.traccarEnabled, settings.traccarUrl, settings.traccarEmail, settings.traccarPassword, settings.traccarRefreshSec, settings.afoulkiEnabled, settings.afoulkiUrl, settings.afoulkiEmail, settings.afoulkiPassword, settings.iopgpsEnabled, settings.iopgpsUrl, settings.iopgpsEmail, settings.iopgpsPassword]);

  const switchMapLayer = (layerKey) => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(tileLayersRef.current).forEach(l => {
      if (map.hasLayer(l)) map.removeLayer(l);
    });
    if (tileLayersRef.current[layerKey]) {
      tileLayersRef.current[layerKey].addTo(map);
    }
    setActiveLayer(layerKey);
    // Force tiles of the new layer to fill the (possibly fullscreen) viewport.
    try { map.invalidateSize(true); } catch (_) {}
    setTimeout(() => { try { map.invalidateSize(true); } catch (_) {} }, 120);
  };

  useEffect(() => {
    let resizeObserver = null;
    let waitTimer = null;
    let leafletRetries = 0;
    let invalidateMap = () => {};
    const onResize = () => invalidateMap();
    const onOrientation = () => { setTimeout(invalidateMap, 250); };
    const onVisibility = () => { if (document.visibilityState === 'visible') setTimeout(invalidateMap, 200); };
    const onOnline = () => setTimeout(invalidateMap, 200);
    const onPageShow = () => setTimeout(invalidateMap, 200);

    const initMap = () => {
      const L = window.L;
      // Leaflet (CDN) may not be ready yet on slow mobile networks — retry until it is.
      if (!L || !mapContainerRef.current) {
        leafletRetries++;
        // ~10s with no Leaflet → it's almost certainly blocked (CSP) or failed to load.
        if (leafletRetries === 40) {
          console.error(
            '[GPS Map] Leaflet (window.L) unavailable after 10s — the script from ' +
            'https://unpkg.com/leaflet may be blocked by CSP or a network failure. ' +
            'Map cannot initialize.'
          );
        }
        waitTimer = setTimeout(initMap, 250);
        return;
      }
      if (leafletRetries >= 40) {
        console.info('[GPS Map] Leaflet became available; initializing map.');
      }
      if (mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
      maxZoom: 20,
      zoomSnap: 0.5,
      zoomDelta: 0.5
    }).setView([32.0, -6.0], 6);

    // Define tile layers
    tileLayersRef.current = {
      streets: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 20,
        maxNativeZoom: 19
      }),
      satellite: L.tileLayer('https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
        attribution: '© Google',
        subdomains: ['0', '1', '2', '3'],
        maxZoom: 20,
        maxNativeZoom: 20
      }),
      hybrid: L.tileLayer('https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
        attribution: '© Google',
        subdomains: ['0', '1', '2', '3'],
        maxZoom: 20,
        maxNativeZoom: 20
      }),
      dark: L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '© CartoDB',
        maxZoom: 20,
        maxNativeZoom: 19
      }),
      terrain: L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenTopoMap',
        maxZoom: 20,
        maxNativeZoom: 17
      })
    };

    // Add default layer
    tileLayersRef.current.streets.addTo(map);

    invalidateMap = () => {
      try { map.invalidateSize(true); } catch {}
    };

    // Multiple delayed passes catch late layout/height computation on mobile.
    [50, 150, 300, 600, 1000, 1800].forEach(d => setTimeout(invalidateMap, d));

    if (typeof ResizeObserver !== 'undefined' && mapContainerRef.current) {
      resizeObserver = new ResizeObserver(() => invalidateMap());
      resizeObserver.observe(mapContainerRef.current);
    }

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientation);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);

    map.on('click', async (e) => {
      if (!pickingRef.current || !selectedCarIdRef.current) return;
      const { lat, lng } = e.latlng;
      if (updateCarRef.current) {
        await updateCarRef.current(selectedCarIdRef.current, {
          gpsLat: lat, gpsLng: lng, gpsUpdated: new Date().toISOString()
        });
      }
      pickingRef.current = false;
      setPickingMode(false);
      setToast(t('gps_toast_updated'));
      setTimeout(() => setToast(''), 3000);
    });

      mapRef.current = map;
    };

    initMap();

    return () => {
      if (waitTimer) clearTimeout(waitTimer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onOrientation);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // ── Status filter (All / Moving / Idle / Parked / Offline) ──
  // Declared before the markers effect because that effect depends on visibleCars.
  const statusCounts = useMemo(() => {
    const c = { all: cars.length, moving: 0, idle: 0, parked: 0, offline: 0 };
    cars.forEach(car => { c[getVehicleStatus(car, settings, nowTick)]++; });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, nowTick, settings.gpsOfflineMin, settings.gpsStopThresholdMin]);

  const visibleCars = useMemo(() => {
    if (statusFilter === 'all') return cars;
    return cars.filter(car => getVehicleStatus(car, settings, nowTick) === statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars, statusFilter, nowTick, settings.gpsOfflineMin, settings.gpsStopThresholdMin]);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};
    const bounds = [];
    const localIsAr = window.currentLang === 'ar';
    const textDir = localIsAr ? 'rtl' : 'ltr';
    const textAlign = localIsAr ? 'right' : 'left';

    visibleCars.forEach(car => {
      if (!car.gpsLat || !car.gpsLng) return;
      const isSelected = car.id === selectedCarId;
      // Color by REAL-TIME status (moving/idle/parked/offline), not rental status.
      const sc = statusColor(getVehicleStatus(car, settings, nowTick), settings);
      const stop = getStopInfo(car, settings, nowTick);
      const longStop = stop.isLongStop;
      // Top-down car icon (inline SVG → no external load, can't 404). Colored by
      // status, rotated to heading via SVG-native transform (works on every browser).
      const course = Number(car.gpsCourse) || 0;
      const size = liteMarkers ? 28 : 34;
      const ringColor = isSelected ? sc : (longStop ? '#ef4444' : null);
      const carSvg = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" style="display:block;overflow:visible;${liteMarkers ? '' : 'filter:drop-shadow(0 1px 2px rgba(0,0,0,0.45));'}" aria-label="vehicle">
        <g transform="rotate(${Math.round(course)} 12 12)">
          <rect x="6.5" y="2" width="11" height="20" rx="3.4" fill="${sc}" stroke="#ffffff" stroke-width="1.4"/>
          <rect x="8.4" y="3.8" width="7.2" height="3.8" rx="1.1" fill="rgba(255,255,255,0.85)"/>
          <rect x="8.4" y="15.4" width="7.2" height="3.6" rx="1.1" fill="rgba(255,255,255,0.55)"/>
          <rect x="4.3" y="5" width="2.1" height="4.2" rx="1" fill="#1f2937"/>
          <rect x="17.6" y="5" width="2.1" height="4.2" rx="1" fill="#1f2937"/>
          <rect x="4.3" y="14" width="2.1" height="4.2" rx="1" fill="#1f2937"/>
          <rect x="17.6" y="14" width="2.1" height="4.2" rx="1" fill="#1f2937"/>
        </g>
      </svg>`;
      const icon = L.divIcon({
        className: `gps-vehicle-marker ${liteMarkers ? 'gps-marker-lite' : ''}`,
        html: `<div style="line-height:0;display:inline-block;border-radius:50%;${ringColor ? `box-shadow:0 0 0 4px ${ringColor}55;` : ''}">${carSvg}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      });
      // No popup card on the map (redundant — details shown in the bottom card,
      // the vehicle list and the Statistics dashboard). Clicking selects the car.
      const marker = L.marker([car.gpsLat, car.gpsLng], { icon, riseOnHover: true, zIndexOffset: isSelected ? 1000 : 0 })
        .addTo(map);
      marker.on('click', () => setSelectedCarId(car.id));
      markersRef.current[car.id] = marker;
      bounds.push([car.gpsLat, car.gpsLng]);
    });
    if (bounds.length > 0 && !selectedCarId) {
      try { map.fitBounds(bounds, { padding: [60, 60], maxZoom: 12 }); } catch(e) {}
    }
  }, [visibleCars, selectedCarId, settings.currency, settings.gpsStopThresholdMin, settings.gpsStopRadiusM, nowTick, liteMarkers]);

  const selectedCar = cars.find(c => c.id === selectedCarId);
  const carsWithGPS = cars.filter(c => c.gpsLat && c.gpsLng);
  const carsWithoutGPS = cars.filter(c => !c.gpsLat || !c.gpsLng);

  const STATUS_FILTERS = [
    { key: 'all', fr: 'Tous', ar: 'الكل', emoji: '🚗' },
    { key: 'moving', fr: 'En route', ar: 'يتحرك', emoji: '🟢' },
    { key: 'idle', fr: 'Ralenti', ar: 'متوقف مؤقتاً', emoji: '🟡' },
    { key: 'parked', fr: 'Stationné', ar: 'متوقف', emoji: '🅿️' },
    { key: 'offline', fr: 'Hors ligne', ar: 'غير متصل', emoji: '⚫' },
  ];

  const centerOnCar = (car) => {
    const map = mapRef.current;
    if (!map || !car.gpsLat) return;
    // Smooth animated fly-to with a high zoom so the vehicle is clearly visible.
    const targetZoom = Math.max(map.getZoom() || 0, 16);
    map.flyTo([car.gpsLat, car.gpsLng], targetZoom, { animate: true, duration: 0.9, easeLinearity: 0.25 });
  };

  const removeGPS = async (carId) => {
    if (!confirm(t('confirm_delete_gps'))) return;
    try {
      await updateCar(carId, { gpsLat: null, gpsLng: null, gpsUpdated: null });
      setSelectedCarId(null);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // ── Flash Route: LOAD base route (backend history, local fallback) ──
  // Runs only when toggled on or the vehicle changes — not on every tick.
  useEffect(() => {
    let alive = true;
    if (!flashRoute || !selectedCarId) { setFlashPoints([]); return; }
    flashFitRef.current = false; // re-fit zoom for the new route
    (async () => {
      const since = Date.now() - 24 * 60 * 60 * 1000; // last 24h trail
      let pts = [];
      try {
        const remote = await fetchTrack({ agencyId, vehicleId: selectedCarId, sinceMs: since });
        pts = remote.length ? remote : getTrack(selectedCarId, since);
      } catch (_) {
        pts = getTrack(selectedCarId, since);
      }
      if (!alive) return;
      const clean = sanitizeTrack(pts);
      setFlashPoints(clean);
      if (clean.length < 2) {
        setToast(isAr ? '⚠️ لا يوجد مسار محفوظ لهذه السيارة' : '⚠️ Aucun trajet enregistré pour ce véhicule');
        setTimeout(() => setToast(''), 2800);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashRoute, selectedCarId]);

  // ── Flash Route: DRAW (base + live extension), auto-fit once, perf-capped ──
  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    if (liveRouteRef.current) { liveRouteRef.current.remove(); liveRouteRef.current = null; }
    if (!flashRoute || !selectedCarId) return;

    // Merge: base points + any newer local points + current live position.
    let pts = flashPoints.slice();
    const lastT = pts.length ? pts[pts.length - 1].t : 0;
    if (lastT) pts = pts.concat(getTrack(selectedCarId, lastT + 1));
    const car = cars.find(c => c.id === selectedCarId);
    if (car && isValidLatLng(Number(car.gpsLat), Number(car.gpsLng))) {
      pts.push({ lat: Number(car.gpsLat), lng: Number(car.gpsLng), t: Date.now() });
    }
    pts = sanitizeTrack(pts);
    let latlngs = downsampleLatLng(pts.map(p => [p.lat, p.lng]), 1500);
    if (latlngs.length < 2) return;

    liveRouteRef.current = L.polyline(latlngs, {
      color: '#2563eb', weight: 4, opacity: 0.85, lineJoin: 'round', smoothFactor: 1.5
    }).addTo(map);

    // Auto-fit the whole route once per activation (not on every live update).
    if (!flashFitRef.current) {
      try { map.fitBounds(liveRouteRef.current.getBounds(), { padding: [40, 40], maxZoom: 16 }); } catch (_) {}
      flashFitRef.current = true;
    }

    return () => {
      if (liveRouteRef.current) { liveRouteRef.current.remove(); liveRouteRef.current = null; }
    };
  }, [flashPoints, cars, nowTick, flashRoute, selectedCarId]);

  // ── Playback control ──
  const stopPlayback = () => {
    const p = playRef.current;
    if (p.timer) { clearInterval(p.timer); p.timer = null; }
    if (p.marker) { p.marker.remove(); p.marker = null; }
    if (p.line) { p.line.remove(); p.line = null; }
    setIsPlaying(false);
  };

  const startPlayback = () => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    const pts = historyPoints.map(p => [p.lat, p.lng]);
    if (pts.length < 2) { setToast(isAr ? 'لا يوجد مسار كافٍ' : 'Trajet insuffisant'); return; }
    stopPlayback();

    const marker = L.circleMarker(pts[0], { radius: 8, color: '#1d4ed8', fillColor: '#3b82f6', fillOpacity: 1, weight: 3 }).addTo(map);
    const line = L.polyline([pts[0]], { color: '#2563eb', weight: 5, opacity: 0.95 }).addTo(map);
    playRef.current = { marker, line, timer: null };
    setIsPlaying(true);
    let i = 0;
    const timer = setInterval(() => {
      i++;
      if (i >= pts.length) { clearInterval(timer); playRef.current.timer = null; setIsPlaying(false); return; }
      marker.setLatLng(pts[i]);
      line.addLatLng(pts[i]);
      map.panTo(pts[i], { animate: true, duration: 0.3 });
    }, 350);
    playRef.current.timer = timer;
  };

  // ── History: draw the stored track for the selected vehicle ──
  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    if (historyLayerRef.current) { historyLayerRef.current.remove(); historyLayerRef.current = null; }
    stopPlayback();
    if (!historyOpen || !selectedCarId) return;

    const latlngs = historyPoints.map(p => [p.lat, p.lng]);
    if (latlngs.length < 1) return;
    const group = L.layerGroup().addTo(map);
    if (latlngs.length >= 2) {
      L.polyline(latlngs, { color: '#7c3aed', weight: 4, opacity: 0.8, dashArray: '6 7', lineJoin: 'round' }).addTo(group);
    }
    L.circleMarker(latlngs[0], { radius: 6, color: '#16a34a', fillColor: '#22c55e', fillOpacity: 1, weight: 2 })
      .bindPopup(isAr ? 'البداية' : 'Départ').addTo(group);
    L.circleMarker(latlngs[latlngs.length - 1], { radius: 6, color: '#dc2626', fillColor: '#ef4444', fillOpacity: 1, weight: 2 })
      .bindPopup(isAr ? 'النهاية' : 'Arrivée').addTo(group);
    historyLayerRef.current = group;
    try { map.fitBounds(L.latLngBounds(latlngs), { padding: [50, 50], maxZoom: 15 }); } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyOpen, historyPoints, selectedCarId]);

  // Stop playback / timers + remove scrub marker on unmount
  useEffect(() => () => {
    stopPlayback();
    if (scrubMarkerRef.current) { try { scrubMarkerRef.current.remove(); } catch (_) {} scrubMarkerRef.current = null; }
  }, []);

  // Format a Date as a datetime-local input value (local time).
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Query GPS points for an exact [sinceMs, untilMs] window (backend, local fallback).
  const runHistoryQuery = async (sinceMs, untilMs) => {
    const carId = selectedCarId;
    if (!carId) { setHistoryPoints([]); return; }
    stopPlayback();
    setHistoryLoading(true);
    try {
      const remote = await fetchTrack({ agencyId: agencyIdRef.current, vehicleId: carId, sinceMs, untilMs });
      const points = remote.length > 0 ? remote : getTrack(carId, sinceMs, untilMs);
      setHistoryPoints(points);
      setScrubIndex(points.length ? points.length - 1 : 0);
    } catch (_) {
      const points = getTrack(carId, sinceMs, untilMs);
      setHistoryPoints(points);
      setScrubIndex(points.length ? points.length - 1 : 0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyQuickRange = (range) => {
    setHistoryRange(range);
    const now = Date.now();
    const span = range === '7d' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    setHistoryFrom(toLocalInput(new Date(now - span)));
    setHistoryTo(toLocalInput(new Date(now)));
    runHistoryQuery(now - span, now);
  };

  const applyCustomRange = () => {
    const fromMs = historyFrom ? new Date(historyFrom).getTime() : NaN;
    const toMs = historyTo ? new Date(historyTo).getTime() : NaN;
    if (isNaN(fromMs) || isNaN(toMs)) {
      setToast(isAr ? 'حدّد تاريخ البداية والنهاية' : 'Sélectionnez les dates début et fin'); setTimeout(() => setToast(''), 2800); return;
    }
    if (fromMs >= toMs) {
      setToast(isAr ? 'الفترة غير صحيحة' : 'Plage de dates invalide'); setTimeout(() => setToast(''), 2800); return;
    }
    setHistoryRange('custom');
    runHistoryQuery(fromMs, toMs);
  };

  const resetHistory = () => {
    stopPlayback();
    setHistoryRange('24h');
    setHistoryPoints([]);
    setScrubIndex(0);
    const now = new Date();
    setHistoryFrom(toLocalInput(new Date(now.getTime() - 24 * 60 * 60 * 1000)));
    setHistoryTo(toLocalInput(now));
  };

  const openHistory = () => {
    if (!selectedCarId) { setToast(isAr ? 'اختر سيارة أولاً' : 'Sélectionnez un véhicule'); setTimeout(() => setToast(''), 2500); return; }
    setHistoryOpen(true);
    if (fullscreen) setFsSidebarOpen(false); // free the space so the list doesn't cover History
    applyQuickRange('24h');
  };

  const toggleFlashRoute = () => {
    if (!selectedCarId) { setToast(isAr ? 'اختر سيارة أولاً' : 'Sélectionnez un véhicule'); setTimeout(() => setToast(''), 2500); return; }
    setFlashRoute(v => !v);
  };

  // Re-run the active range when the selected vehicle changes (panel open).
  useEffect(() => {
    if (!historyOpen || !selectedCarId) return;
    if (historyRange === 'custom') applyCustomRange();
    else applyQuickRange(historyRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarId]);

  // Scrubber: place/move a marker at the point for the current time index.
  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L) return;
    if (!historyOpen || historyPoints.length === 0 || isPlaying) {
      if (scrubMarkerRef.current) { scrubMarkerRef.current.remove(); scrubMarkerRef.current = null; }
      return;
    }
    const i = Math.min(Math.max(0, scrubIndex), historyPoints.length - 1);
    const p = historyPoints[i];
    if (!p) return;
    if (!scrubMarkerRef.current) {
      scrubMarkerRef.current = L.circleMarker([p.lat, p.lng], { radius: 8, color: '#1d4ed8', fillColor: '#3b82f6', fillOpacity: 1, weight: 3 }).addTo(map);
    } else {
      scrubMarkerRef.current.setLatLng([p.lat, p.lng]);
    }
  }, [scrubIndex, historyPoints, historyOpen, isPlaying]);

  // ── Fullscreen map ──
  // Re-measure the map after a size change and KEEP the view, so tiles fully
  // re-render instead of staying gray/frozen ("static") when toggling fullscreen.
  const recalcMap = () => {
    const map = mapRef.current;
    if (!map) return;
    let center, zoom;
    try { center = map.getCenter(); zoom = map.getZoom(); } catch (_) {}
    const fix = () => {
      const m = mapRef.current;
      if (!m) return;
      try {
        m.invalidateSize(true);
        if (center) m.setView(center, zoom, { animate: false });
      } catch (_) {}
    };
    // Immediate (rAF) + several delayed passes to cover CSS/layout settle + iOS.
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => requestAnimationFrame(fix));
    }
    [80, 200, 400, 700, 1100].forEach(d => setTimeout(fix, d));
  };

  const toggleFullscreen = () => {
    const next = !fullscreen;
    setFullscreen(next);
    try {
      const el = mapShellRef.current;
      if (next && el?.requestFullscreen) el.requestFullscreen().catch(() => {});
      else if (!next && document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    } catch (_) {}
    recalcMap();
  };

  const togglePerfMode = () => setLiteMarkers(v => !v);

  // Lock page scroll + sync state with native fullscreen + ESC to exit.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = fullscreen ? 'hidden' : prevOverflow;

    const onKey = (e) => { if (e.key === 'Escape' && fullscreen) { setFullscreen(false); recalcMap(); } };
    const onFsChange = () => {
      // User exited native fullscreen (ESC/gesture) → sync our CSS state.
      if (!document.fullscreenElement && fullscreen) { setFullscreen(false); recalcMap(); }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [fullscreen]);

  return (
    <div className="gps-root flex flex-col h-[calc(100vh-120px)] gap-4 overflow-x-hidden max-w-full">
      {/* Header Info */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-2xl font-black text-gray-900 leading-none">{t('gps_tracking')}</h1>
          <p className="text-xs text-gray-400 mt-1.5">
            {t('gps_stats')
              .replace('{localized}', carsWithGPS.length)
              .replace('{not_localized}', carsWithoutGPS.length)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Traccar Status */}
          {settings.traccarEnabled && (
            <div 
              className={`border rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-2 transition ${
                traccarStatus.connected 
                  ? 'bg-green-50 border-green-200 text-green-700' 
                  : (traccarStatus.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700')
              }`}
              title={traccarStatus.error || (traccarStatus.lastSync ? `Sync: ${traccarStatus.lastSync.toLocaleTimeString()}` : 'Attente...')}
            >
              <span className={`w-2 h-2 rounded-full ${
                traccarStatus.connected ? 'bg-green-500 animate-pulse' : (traccarStatus.error ? 'bg-red-600' : 'bg-amber-500')
              }`} />
              {traccarStatus.connected 
                ? `📡 Traccar (${traccarStatus.devicesCount || 0} ${isAr ? 'أجهزة' : 'devices'})`
                : (traccarStatus.error ? '❌ Traccar erreur' : '⏳ Connexion...')}
            </div>
          )}
          {pickingMode && (
            <div className="bg-amber-50 border-2 border-amber-500 rounded-xl px-4 py-1.5 text-xs font-bold text-amber-700 flex items-center gap-2 animate-pulse">
              {t('gps_picking_mode')}
              <button 
                onClick={() => setPickingMode(false)}
                className="hover:text-amber-950 font-black text-sm"
              >
                ✕
              </button>
            </div>
          )}
          {toast && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-1.5 text-xs font-bold text-green-700">
              {toast}
            </div>
          )}
        </div>
      </div>

      {/* Main GPS Layout */}
      <div className="flex gap-4 flex-1 min-h-0 min-w-0 gps-layout">
        {/* Sidebar cars list (hidden in fullscreen) */}
        <div className={`w-64 flex-shrink-0 min-w-0 bg-white border border-gray-200 rounded-2xl p-3 overflow-y-auto gps-sidebar ${fullscreen ? 'hidden' : ''}`}>
          <div className="px-2 py-1 pb-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            {t('gps_cars_label').replace('{count}', visibleCars.length)}
          </div>
          <div className="space-y-1 gps-car-list">
            {visibleCars.map(car => {
              const isSelected = car.id === selectedCarId;
              const hasGPS = !!(car.gpsLat && car.gpsLng);
              const sc = car.status === 'available' ? '#10b981' : car.status === 'rented' ? '#3b82f6' : '#f59e0b';
              const dispoLabel = car.status === 'available' ? (isAr ? 'متاحة' : 'Dispo') : car.status === 'rented' ? (isAr ? 'مؤجرة' : 'Louée') : (isAr ? 'صيانة' : 'Maint.');
              const stop = hasGPS ? getStopInfo(car, settings, nowTick) : null;

              return (
                <div
                  key={car.id}
                  onClick={() => { setSelectedCarId(car.id); if (hasGPS) centerOnCar(car); }}
                  className={`gps-car-card p-3 rounded-xl cursor-pointer border transition-all ${
                    isSelected
                      ? 'bg-blue-50/50 border-blue-500 shadow-sm ring-2 ring-blue-100/50'
                      : stop?.isLongStop
                        ? 'border-red-300 bg-red-50/40 hover:bg-red-50'
                        : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-bold text-sm text-gray-900">{car.brand} {car.model}</div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: hasGPS ? statusColor(getVehicleStatus(car, settings, nowTick), settings) : '#d1d5db' }}
                        title={hasGPS ? getVehicleStatus(car, settings, nowTick) : 'offline'}
                      />
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: `${sc}15`, color: sc }}
                      >
                        {dispoLabel}
                      </span>
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-400 font-mono font-semibold">{car.plate}</div>

                  {hasGPS ? (
                    <div className="mt-2 space-y-1 gps-card-extra">
                      <div className="text-[10px] text-green-600 font-mono font-bold">
                        📍 {car.gpsLat.toFixed(4)}, {car.gpsLng.toFixed(4)}
                      </div>
                      {car.gpsSource === 'traccar' && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-[8px] font-bold px-1 py-0.5 bg-green-100 text-green-800 rounded">📡 Traccar</span>
                          {car.gpsSpeed !== undefined && car.gpsSpeed > 1 && (
                            <span className="text-[8px] font-bold px-1 py-0.5 bg-blue-100 text-blue-800 rounded">
                              {/* GPSWOX returns speed already in kph — display directly */}
                              {Math.round(car.gpsSpeed)} kph
                            </span>
                          )}
                          {car.gpsDeviceStatus && (
                            <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${
                              car.gpsDeviceStatus === 'online' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {car.gpsDeviceStatus === 'online' ? '🟢 Online' : '🔴 Offline'}
                            </span>
                          )}
                          {car.gpsKmDaily > 0 && (
                            <span className="text-[8px] font-bold px-1 py-0.5 bg-amber-100 text-amber-800 rounded">
                              🛣️ {car.gpsKmDaily.toFixed(1)} km
                            </span>
                          )}
                          {car.gpsOdometer > 0 && (
                            <span className="text-[8px] font-bold px-1 py-0.5 bg-indigo-100 text-indigo-800 rounded">
                              📊 {car.gpsOdometer.toFixed(0)} km
                            </span>
                          )}
                        </div>
                      )}

                      {/* Stop / movement status */}
                      {stop && (
                        <div className="flex flex-col gap-0.5 pt-0.5">
                          <div className="flex items-center gap-1 flex-wrap">
                            {stop.moving ? (
                              <span className="text-[8px] font-bold px-1 py-0.5 bg-green-100 text-green-800 rounded">
                                🟢 {isAr ? 'يتحرك' : 'En route'}
                              </span>
                            ) : (
                              <span className={`text-[8px] font-bold px-1 py-0.5 rounded ${stop.isLongStop ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700'}`}>
                                ⏸️ {isAr ? 'متوقف' : 'Arrêté'}{stop.stoppedMs > 0 ? ` · ${formatDuration(stop.stoppedMs, isAr)}` : ''}
                              </span>
                            )}
                            {stop.isLongStop && (
                              <span className="text-[8px] font-bold px-1 py-0.5 bg-red-600 text-white rounded">
                                ⚠️ {isAr ? 'توقف طويل' : 'Arrêt long'}
                              </span>
                            )}
                          </div>
                          {stop.lastMovedAt && (
                            <div className="text-[8px] text-gray-400">
                              {isAr ? 'آخر حركة' : 'Dern. mouvement'}: {new Date(stop.lastMovedAt).toLocaleString(isAr ? 'ar-MA' : 'fr-FR')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-[10px] text-gray-400 mt-2 gps-card-extra">
                      {car.traccarUniqueId ? `⏳ Attente Traccar (${car.traccarUniqueId})` : t('gps_unknown_pos')}
                    </div>
                  )}

                  <div className="mt-2 flex gap-1 gps-card-extra">
                    <button
                      onClick={(e) => { e.stopPropagation(); setStatsCar(car); }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100 transition"
                    >
                      📊 {isAr ? 'الإحصائيات' : 'Statistiques'}
                    </button>
                    {car.status !== 'maintenance' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const note = window.prompt(isAr ? 'وصف العطل (اختياري):' : 'Description de la panne (optionnel):', '');
                          if (note === null) return; // cancelled
                          try {
                            await reportBreakdown({ car, comment: note || '', severity: 'medium', settings, lang, addBreakdown, updateCar });
                            setToast(isAr ? '🛠️ تم الإبلاغ عن العطل — المركبة قيد الإصلاح' : '🛠️ Panne signalée — véhicule en réparation');
                            setTimeout(() => setToast(''), 4000);
                          } catch (err) {
                            setToast('❌ ' + err.message); setTimeout(() => setToast(''), 4000);
                          }
                        }}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-50 text-red-700 border border-red-100 hover:bg-red-100 transition"
                        title={isAr ? 'الإبلاغ عن عطل' : 'Signaler une panne'}
                      >
                        🛠️ {isAr ? 'عطل' : 'Panne'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {visibleCars.length === 0 && (
              <p className="text-center py-6 text-gray-400 text-xs italic">
                {cars.length === 0 ? t('gps_no_cars') : (isAr ? 'لا توجد سيارات في هذا الفلتر' : 'Aucun véhicule dans ce filtre')}
              </p>
            )}
          </div>
        </div>

        {/* City geofence history panel removed from UI.
            Detection + Telegram alerts remain fully active in the sync loop;
            events are still persisted via addGeofenceEvent (geoNotifHistory). */}

        {/* Map area */}
        <div className="flex-1 flex flex-col gap-4 min-w-0 min-h-[350px] relative gps-map-area">
          <div
            ref={mapShellRef}
            className={`flex-1 min-h-[350px] md:min-h-0 relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm z-10 ${fullscreen ? 'gps-map-fullscreen' : ''}`}
          >
            <div ref={mapContainerRef} className="absolute inset-0 w-full h-full bg-gray-50" />

            {/* Compact map controls: fullscreen + layers dropdown */}
            <div className="map-type-control absolute top-3 right-3 z-[1000] flex items-start gap-1.5">
              <button
                onClick={toggleFullscreen}
                className={`flex items-center justify-center w-9 h-9 rounded-xl text-sm font-bold shadow-lg border backdrop-blur-md transition-all ${fullscreen ? 'bg-blue-600 text-white border-blue-700' : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'}`}
                title={fullscreen ? (isAr ? 'خروج من ملء الشاشة' : 'Quitter le plein écran') : (isAr ? 'ملء الشاشة' : 'Plein écran')}
                aria-label="Fullscreen"
                aria-pressed={fullscreen}
              >
                {fullscreen ? '✕' : '⛶'}
              </button>

              <div className="relative">
                <button
                  onClick={() => setLayersOpen(o => !o)}
                  className={`flex items-center gap-1 px-2.5 h-9 rounded-xl text-sm font-bold shadow-lg border backdrop-blur-md transition-all ${layersOpen ? 'bg-blue-600 text-white border-blue-700' : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'}`}
                  title={isAr ? 'طبقات الخريطة' : 'Couches de carte'}
                  aria-label="Map layers"
                  aria-expanded={layersOpen}
                >
                  <span className="text-base leading-none">🗂️</span>
                  <span className="text-[11px] leading-none">{(mapLayers.find(l => l.key === activeLayer) || mapLayers[0]).icon}</span>
                </button>

                {layersOpen && (
                  <div className="absolute top-full right-0 mt-1.5 w-44 bg-white rounded-xl border border-gray-200 shadow-xl p-1 z-[1001]">
                    {mapLayers.map(layer => (
                      <button
                        key={layer.key}
                        onClick={() => { switchMapLayer(layer.key); setLayersOpen(false); }}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-bold transition ${
                          activeLayer === layer.key ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="text-sm leading-none">{layer.icon}</span>
                        <span>{layer.label}</span>
                        {activeLayer === layer.key && <span className="ms-auto text-xs">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Route tools (Flash Route + History) — top-left, near the map.
                Shift right only on desktop when the left list panel is open. */}
            <div className={`gps-route-tools absolute top-3 left-3 z-[1001] flex flex-col gap-1.5 ${fullscreen && fsSidebarOpen ? 'fs-shift' : ''}`}>
              {/* Compact status filter (expandable overlay) */}
              <div className="relative">
                {(() => {
                  const af = STATUS_FILTERS.find(f => f.key === statusFilter) || STATUS_FILTERS[0];
                  return (
                    <button
                      onClick={() => setFiltersOpen(o => !o)}
                      aria-expanded={filtersOpen}
                      title={isAr ? 'تصفية حسب الحالة' : 'Filtrer par statut'}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg border transition-all backdrop-blur-md ${
                        statusFilter !== 'all' ? 'bg-blue-600 text-white border-blue-700' : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'
                      }`}
                    >
                      <span>{af.emoji}</span>
                      <span>{isAr ? af.ar : af.fr}</span>
                      <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${statusFilter !== 'all' ? 'bg-white/25' : 'bg-gray-100 text-gray-600'}`}>
                        {statusCounts[statusFilter] ?? 0}
                      </span>
                      <span className="text-[9px] opacity-70">{filtersOpen ? '▲' : '▼'}</span>
                    </button>
                  );
                })()}
                {filtersOpen && (
                  <div className="absolute top-full left-0 mt-1.5 w-48 bg-white rounded-xl border border-gray-200 shadow-xl p-1 z-[1002] gps-filter-pop">
                    {STATUS_FILTERS.map(f => {
                      const active = statusFilter === f.key;
                      return (
                        <button
                          key={f.key}
                          onClick={() => { setStatusFilter(f.key); setFiltersOpen(false); }}
                          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-bold transition ${active ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          <span className="text-sm leading-none">{f.emoji}</span>
                          <span className="flex-1 text-start">{isAr ? f.ar : f.fr}</span>
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${active ? 'bg-white/25' : 'bg-gray-100 text-gray-600'}`}>
                            {statusCounts[f.key] ?? 0}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                onClick={toggleFlashRoute}
                title={isAr ? 'مسار مباشر' : 'Itinéraire en direct'}
                aria-pressed={flashRoute}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg border transition-all backdrop-blur-md ${
                  flashRoute
                    ? 'bg-blue-600 text-white border-blue-700'
                    : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'
                } ${!selectedCarId ? 'opacity-60' : ''}`}
              >
                <span className="text-sm leading-none">📍</span>
                <span>Flash Route</span>
                {flashRoute && <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />}
              </button>
              <button
                onClick={openHistory}
                title={isAr ? 'سجل المسارات' : 'Historique des trajets'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg border transition-all backdrop-blur-md ${
                  historyOpen
                    ? 'bg-purple-600 text-white border-purple-700'
                    : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'
                } ${!selectedCarId ? 'opacity-60' : ''}`}
              >
                <span className="text-sm leading-none">📅</span>
                <span>{isAr ? 'السجل' : 'History'}</span>
              </button>
              <button
                onClick={togglePerfMode}
                title={liteMarkers ? (isAr ? 'جودة عالية' : 'Qualité élevée') : (isAr ? 'وضع الأداء' : 'Mode performance')}
                aria-pressed={liteMarkers}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold shadow-lg border transition-all backdrop-blur-md ${
                  liteMarkers
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : 'bg-white/95 text-gray-700 border-gray-200/70 hover:bg-gray-100'
                }`}
              >
                <span className="text-sm leading-none">⚡</span>
                <span>{liteMarkers ? (isAr ? 'أداء' : 'Perf') : 'HD'}</span>
              </button>
            </div>

            {/* Fullscreen floating speedometer — always visible over the map on
                every device (incl. mobile, where the bottom detail card is covered).
                Uses the selected vehicle, or falls back to the first tracked one. */}
            {fullscreen && (selectedCar || carsWithGPS[0]) && (() => {
              const fc = selectedCar || carsWithGPS[0];
              const status = getVehicleStatus(fc, settings, nowTick);
              const speed = Number(fc.gpsSpeed) || 0;
              const limit = settings.gpsSpeedLimit ?? 120;
              const gear = status === 'moving'
                ? { l: 'D', c: '#16a34a', t: isAr ? 'سير' : 'Drive' }
                : status === 'idle'
                ? { l: 'N', c: '#f59e0b', t: isAr ? 'محايد' : 'Neutre' }
                : status === 'parked'
                ? { l: 'P', c: '#475569', t: isAr ? 'وقوف' : 'Park' }
                : { l: '—', c: '#6b7280', t: isAr ? 'غير متصل' : 'Hors-ligne' };

              // ── Driving-risk indicator ──────────────────────────────────
              // Combines over-speed (vs limit) with harsh acceleration/braking
              // detected from the speed delta between polls. Only meaningful
              // while moving; parked/offline shows neutral (safe).
              const ratio = limit > 0 ? speed / limit : 0;
              const nowMs = Date.now();
              const pr = riskRef.current;
              let accel = 0; // km/h per second
              if (pr.id === fc.id && pr.t) {
                const dt = (nowMs - pr.t) / 1000;
                if (dt > 0.3 && dt < 20) accel = (speed - pr.speed) / dt;
              }
              riskRef.current = { id: fc.id, speed, t: nowMs };
              const harshAccel = accel > 11;   // hard acceleration
              const harshBrake = accel < -16;  // hard braking
              let risk = 'safe';
              if (status === 'moving') {
                if (ratio >= 1.0 || harshBrake) risk = 'risky';
                else if (ratio >= 0.85 || harshAccel) risk = 'caution';
              }
              const riskMeta = risk === 'risky'
                ? { c: '#dc2626', t: isAr ? 'خطر' : 'Risque' }
                : risk === 'caution'
                ? { c: '#f59e0b', t: isAr ? 'انتبه' : 'Prudence' }
                : { c: '#16a34a', t: isAr ? 'آمن' : 'Sûr' };

              return (
                // Compact, FIXED right-center control panel (Waze-like). Fixed
                // px size so it never scales with fullscreen; above the map.
                <div className={`gps-fs-panel risk-${risk}`}>
                  <div className="gps-fs-risk" style={{ color: riskMeta.c }}>
                    <span className="gps-fs-risk-dot" style={{ background: riskMeta.c }} />
                    <span className="gps-fs-risk-text">{riskMeta.t}</span>
                  </div>
                  <SpeedGauge speed={speed} limit={limit} isAr={isAr} size={84} />
                  <div className="gps-fs-gear">
                    <span className="gps-fs-gear-letter" style={{ color: gear.c }}>{gear.l}</span>
                    <span className="gps-fs-gear-text">{gear.t}</span>
                  </div>
                </div>
              );
            })()}

            {/* Fullscreen: floating vehicle list over the map (blurred glass).
                Desktop = left vertical panel; mobile = bottom horizontal slider. */}
            {fullscreen && (
              <>
                <div className={`gps-fs-list ${fsSidebarOpen ? 'fs-open' : 'fs-closed'}`}>
                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200/60">
                    <span className="text-xs font-black text-gray-800 flex items-center gap-1.5">
                      <span>🚗</span>
                      <span>{t('gps_cars_label').replace('{count}', visibleCars.length)}</span>
                    </span>
                    <button
                      onClick={() => setFsSidebarOpen(false)}
                      className="p-1 rounded-lg hover:bg-gray-200/60 text-gray-600"
                      title={isAr ? 'إخفاء' : 'Masquer'}
                      aria-label="Hide list"
                    >
                      <span className="text-sm">‹</span>
                    </button>
                  </div>
                  <div className="gps-fs-items flex-1 overflow-y-auto p-2 space-y-1">
                    {visibleCars.map(car => {
                      const isSel = car.id === selectedCarId;
                      const hasGPS = !!(car.gpsLat && car.gpsLng);
                      const sc = car.status === 'available' ? '#10b981' : car.status === 'rented' ? '#3b82f6' : '#f59e0b';
                      const st = hasGPS ? getStopInfo(car, settings, nowTick) : null;
                      return (
                        <button
                          key={car.id}
                          onClick={() => { setSelectedCarId(car.id); if (hasGPS) centerOnCar(car); setFsSidebarOpen(false); }}
                          className={`gps-fs-card w-full text-start p-2 rounded-xl border transition-colors ${
                            isSel ? 'bg-blue-50/80 border-blue-400' : 'bg-white/60 border-transparent hover:bg-white/90'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-xs text-gray-900 truncate">{car.brand} {car.model}</span>
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: hasGPS ? statusColor(getVehicleStatus(car, settings, nowTick), settings) : '#d1d5db' }} />
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <span className="text-[10px] font-mono text-gray-500 truncate">{car.plate}</span>
                            {st && (
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${st.moving ? 'bg-green-100 text-green-700' : st.isLongStop ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'}`}>
                                {st.moving ? (isAr ? 'يتحرك' : 'En route') : (st.stoppedMs > 0 ? formatDuration(st.stoppedMs, isAr) : (isAr ? 'متوقف' : 'Arrêté'))}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {visibleCars.length === 0 && (
                      <p className="text-center py-6 text-gray-400 text-xs italic">
                        {cars.length === 0 ? t('gps_no_cars') : (isAr ? 'لا توجد سيارات في هذا الفلتر' : 'Aucun véhicule dans ce filtre')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Reopen tab when the list is collapsed (left on desktop, bottom on mobile) */}
                {!fsSidebarOpen && (
                  <button
                    onClick={() => setFsSidebarOpen(true)}
                    className="gps-fs-reopen z-[1100] bg-white/90 backdrop-blur-md border border-white/60 shadow-lg text-gray-700 font-bold"
                    title={isAr ? 'عرض السيارات' : 'Afficher les véhicules'}
                    aria-label="Show list"
                  >
                    🚗 {visibleCars.length}
                  </button>
                )}
              </>
            )}

            {/* History panel — bottom-left over the map (kept above the fullscreen list) */}
            {historyOpen && (
              <div className="absolute bottom-3 left-3 z-[1200] w-[270px] max-w-[calc(100%-1.5rem)] max-h-[78vh] overflow-y-auto bg-white rounded-2xl border border-gray-200 shadow-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-black text-gray-800 flex items-center gap-1.5">
                    <span>📅</span>
                    <span>{isAr ? 'سجل المسارات' : 'Historique trajet'}</span>
                  </div>
                  <button
                    onClick={() => { setHistoryOpen(false); }}
                    className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
                    aria-label="Close"
                  >
                    <span className="text-sm">✕</span>
                  </button>
                </div>

                <div className="text-[10px] text-gray-500 font-semibold mb-2 truncate">
                  🚗 {selectedCar ? `${selectedCar.brand} ${selectedCar.model}` : '-'}
                </div>

                {/* Quick ranges */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  {[{ k: '24h', fr: '24 h', ar: '24 س' }, { k: '7d', fr: '7 jours', ar: '7 أيام' }].map(r => (
                    <button
                      key={r.k}
                      onClick={() => applyQuickRange(r.k)}
                      className={`px-2 py-1.5 rounded-lg text-[11px] font-bold border transition ${
                        historyRange === r.k
                          ? 'bg-purple-600 text-white border-purple-700'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {isAr ? r.ar : r.fr}
                    </button>
                  ))}
                </div>

                {/* Custom From – To range */}
                <div className="space-y-1.5 mb-2 p-2 rounded-xl bg-gray-50 border border-gray-100">
                  <label className="block text-[10px] font-bold text-gray-500">
                    {isAr ? 'من' : 'De'}
                    <input
                      type="datetime-local"
                      value={historyFrom}
                      onChange={e => { setHistoryFrom(e.target.value); setHistoryRange('custom'); }}
                      className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-[11px] bg-white outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </label>
                  <label className="block text-[10px] font-bold text-gray-500">
                    {isAr ? 'إلى' : 'À'}
                    <input
                      type="datetime-local"
                      value={historyTo}
                      onChange={e => { setHistoryTo(e.target.value); setHistoryRange('custom'); }}
                      className="mt-0.5 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-[11px] bg-white outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </label>
                  <div className="flex gap-1.5 pt-0.5">
                    <button
                      onClick={applyCustomRange}
                      className="flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold bg-purple-600 text-white hover:bg-purple-700"
                    >
                      {isAr ? 'تطبيق' : 'Appliquer'}
                    </button>
                    <button
                      onClick={resetHistory}
                      className="flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                    >
                      {isAr ? 'إعادة' : 'Réinit.'}
                    </button>
                  </div>
                </div>

                {/* Count + playback */}
                <div className="flex items-center justify-between gap-2 mb-1.5 px-0.5">
                  <span className="text-[10px] text-gray-500 font-semibold">
                    {historyLoading ? (isAr ? 'جاري التحميل…' : 'Chargement…') : `${historyPoints.length} ${isAr ? 'نقطة' : 'points'}`}
                  </span>
                  {!isPlaying ? (
                    <button
                      onClick={startPlayback}
                      disabled={historyPoints.length < 2}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                    >
                      ▶ {isAr ? 'تشغيل' : 'Lire'}
                    </button>
                  ) : (
                    <button
                      onClick={stopPlayback}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-gray-700 text-white hover:bg-gray-800"
                    >
                      ⏹ {isAr ? 'إيقاف' : 'Stop'}
                    </button>
                  )}
                </div>

                {/* Time scrubber */}
                {historyPoints.length > 0 && (() => {
                  const idx = Math.min(scrubIndex, historyPoints.length - 1);
                  const cur = historyPoints[idx] || {};
                  return (
                    <div className="mb-1">
                      <input
                        type="range"
                        min={0}
                        max={historyPoints.length - 1}
                        value={idx}
                        onChange={e => { stopPlayback(); setScrubIndex(parseInt(e.target.value)); }}
                        className="w-full accent-blue-600"
                      />
                      <div className="flex items-center justify-between text-[10px] mt-0.5">
                        <span className="font-bold text-blue-700">
                          🕐 {cur.t ? new Date(cur.t).toLocaleString(isAr ? 'ar-MA' : 'fr-FR') : '-'}
                        </span>
                        <span className="text-gray-500 font-semibold">{Math.round(cur.s || 0)} kph</span>
                      </div>
                      <div className="text-[9px] text-gray-400 mt-0.5">
                        {isAr ? 'النقطة' : 'Point'} {idx + 1}/{historyPoints.length}
                      </div>
                    </div>
                  );
                })()}

                {!historyLoading && historyPoints.length === 0 && (
                  <p className="text-[10px] text-gray-400 italic text-center py-1">
                    {isAr ? 'لا يوجد مسار محفوظ لهذه الفترة' : 'Aucun trajet enregistré sur cette période'}
                  </p>
                )}
                <div className="flex items-center gap-3 text-[9px] text-gray-500 mt-1 px-0.5 flex-wrap">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{isAr ? 'بداية' : 'Départ'}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{isAr ? 'نهاية' : 'Arrivée'}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />{isAr ? 'الموضع' : 'Position'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Bottom detail action card */}
          {selectedCar ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 shadow-sm flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center font-bold text-lg shrink-0">🚗</div>
                <div className="min-w-0">
                  <div className="font-bold text-gray-900 text-sm sm:text-base truncate">{selectedCar.brand} {selectedCar.model}</div>
                  <div className="flex items-center gap-2 mt-0.5 sm:mt-1 flex-wrap">
                    <span className="text-xs text-gray-400 font-semibold">{selectedCar.plate}</span>
                    {selectedCar.gpsUpdated && (
                      <span className="hidden sm:inline text-[10px] text-gray-300 font-mono">
                        • {new Date(parseGpsTime(selectedCar.gpsUpdated) || Date.parse(selectedCar.gpsUpdated)).toLocaleString(isAr ? 'ar-MA' : 'fr-FR')}
                      </span>
                    )}
                  </div>

                  {/* Dashboard readout: odometer (like a car), speed, today, status */}
                  {(() => {
                    const odo = Math.round(Number(selectedCar.gpsOdometer) || Number(selectedCar.km) || 0);
                    const sp = Math.round(Number(selectedCar.gpsSpeed) || 0);
                    const today = (Number(selectedCar.gpsKmDaily) || 0).toFixed(1);
                    const st = (selectedCar.gpsLat && selectedCar.gpsLng) ? getStopInfo(selectedCar, settings, nowTick) : null;
                    return (
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span
                          className="inline-flex items-center gap-1 font-mono font-black text-[11px] tracking-widest text-emerald-300 bg-gray-900 px-2 py-1 rounded-md shadow-inner"
                          title={isAr ? 'عداد المسافة الكلي' : 'Odomètre (total)'}
                        >
                          🔢 {odo.toLocaleString()} <span className="text-emerald-500 text-[9px]">km</span>
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded-md">
                          💨 {sp} kph
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded-md" title={isAr ? 'مسافة اليوم' : "Distance aujourd'hui"}>
                          📅 {today} km
                        </span>
                        {st && (
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${st.moving ? 'bg-green-100 text-green-700' : st.isLongStop ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'}`}>
                            {st.moving ? (isAr ? '🟢 يتحرك' : '🟢 En route') : (st.stoppedMs > 0 ? `⏸️ ${formatDuration(st.stoppedMs, isAr)}` : (isAr ? '⏸️ متوقف' : '⏸️ Arrêté'))}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Live speedometer — desktop only; hidden on mobile to maximize the map */}
              <div className="hidden sm:flex flex-col items-center justify-center px-2 order-last sm:order-none w-full sm:w-auto">
                <SpeedGauge
                  speed={selectedCar.gpsSpeed || 0}
                  limit={settings.gpsSpeedLimit ?? 120}
                  isAr={isAr}
                />
              </div>

              <div className="flex flex-wrap gap-2 flex-shrink-0 w-full sm:w-auto sm:justify-end">
                <button
                  onClick={() => setStatsCar(selectedCar)}
                  className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-300 px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-1 sm:flex-none justify-center"
                  title={isAr ? 'إحصائيات السيارة' : 'Statistiques du véhicule'}
                >
                  <span>📊</span>
                  <span>{isAr ? 'إحصائيات' : 'Statistiques'}</span>
                </button>
                {selectedCar.traccarUniqueId && (
                  <button
                    onClick={() => setEngineControlCar(selectedCar)}
                    className="bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-1 sm:flex-none justify-center"
                    title={isAr ? 'التحكم في مضخة البنزين' : 'Contrôle Pompe Essence'}
                  >
                    <span>⚡</span>
                    <span>{isAr ? 'التحكم' : 'Moteur'}</span>
                  </button>
                )}
                <button
                  onClick={() => setPickingMode(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold transition shadow-sm flex-1 sm:flex-none justify-center"
                >
                  {t('gps_set_position')}
                </button>
                {selectedCar.gpsLat && (
                  <button
                    onClick={() => centerOnCar(selectedCar)}
                    className="bg-gray-150 hover:bg-gray-200 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl text-xs font-bold transition flex-1 sm:flex-none justify-center"
                  >
                    {t('gps_center')}
                  </button>
                )}
                {selectedCar.gpsLat && (
                  <button
                    onClick={() => removeGPS(selectedCar.id)}
                    className="bg-red-50 hover:bg-red-100 text-red-650 text-red-650 px-4 py-2 rounded-xl text-xs font-bold transition border border-red-200/50 flex-1 sm:flex-none justify-center"
                  >
                    {t('gps_delete')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 border-dashed rounded-2xl p-6 text-center text-xs text-gray-400 flex-shrink-0">
              {t('gps_select_hint')}
            </div>
          )}
        </div>
      </div>

      {engineControlCar && (
        <EngineControlModal
          car={engineControlCar}
          settings={settings}
          onClose={() => setEngineControlCar(null)}
        />
      )}

      {statsCar && (
        <VehicleStatsModal
          car={statsCar}
          settings={settings}
          agencyId={agencyId}
          onClose={() => setStatsCar(null)}
        />
      )}

    </div>
  );
}

// ── Speedometer gauge (vehicle-dashboard style, pure SVG) ─────────────────────
function SpeedGauge({ speed = 0, limit = 120, isAr = false, size = 116 }) {
  const safeLimit = limit || 120;
  // Round the scale max up to a clean value above the limit.
  const max = Math.max(160, Math.ceil((safeLimit + 40) / 20) * 20);
  const val = Math.max(0, Math.round(speed || 0));
  const frac = max > 0 ? Math.min(1, val / max) : 0;
  const over = val > safeLimit;
  const near = !over && val > safeLimit * 0.85;
  const numColor = over ? '#f87171' : near ? '#fbbf24' : '#ffffff';

  const cx = 50, cy = 50;
  const ringR = 41;                 // tick ring radius
  const T = 40;                     // number of ticks (full circle)
  const filled = Math.round(T * frac);

  // Full ring of ticks; the active portion (from the bottom, clockwise) is
  // colored green→orange→red by the speed each tick represents.
  const ticks = [];
  for (let i = 0; i < T; i++) {
    const deg = 90 + (360 * i) / T;            // start at bottom, go clockwise
    const a = (deg * Math.PI) / 180;
    const x1 = cx + ringR * Math.cos(a);
    const y1 = cy + ringR * Math.sin(a);
    const x2 = cx + (ringR - 5) * Math.cos(a);
    const y2 = cy + (ringR - 5) * Math.sin(a);
    let stroke = '#7c8696';                    // inactive tick
    if (i < filled) {
      const tickSpeed = (i / T) * max;
      stroke = tickSpeed > safeLimit ? '#ef4444' : tickSpeed > safeLimit * 0.85 ? '#f59e0b' : '#22c55e';
    }
    ticks.push(<line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth="2.4" strokeLinecap="round" />);
  }

  return (
    <svg viewBox="0 0 100 100" width={size} height={size} role="img"
      aria-label={`${val} km/h`} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <radialGradient id="sgDisc" cx="50%" cy="36%" r="72%">
          <stop offset="0%" stopColor="#464f5e" />
          <stop offset="100%" stopColor="#2a313d" />
        </radialGradient>
      </defs>
      {/* dark dial */}
      <circle cx={cx} cy={cy} r="46" fill="url(#sgDisc)" stroke="#1f2632" strokeWidth="1.5" />
      <ellipse cx="50" cy="30" rx="30" ry="13" fill="#ffffff" opacity="0.06" />
      {ticks}
      {/* speed value + unit */}
      <text x={cx} y="46" textAnchor="middle" dominantBaseline="middle" fill={numColor}
        style={{ fontSize: 25, fontWeight: 800 }}>{val}</text>
      <text x={cx} y="66" textAnchor="middle" fill="#cbd5e1"
        style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>km/h</text>
      {/* speed-limit roundel — shown when over the limit (Waze style) */}
      {over && (
        <g>
          <circle cx="80" cy="22" r="15" fill="#ffffff" stroke="#ef4444" strokeWidth="5" />
          <text x="80" y="22" textAnchor="middle" dominantBaseline="middle" fill="#111827"
            style={{ fontSize: 14, fontWeight: 800 }}>{safeLimit}</text>
        </g>
      )}
    </svg>
  );
}
